import {z} from 'zod';
import {t} from 'i18next';
import {
  FormAction,
  ModuleType,
  ReProductionType,
} from '../../components/productions/re-production/ReProductionUtils';
import {qualityControlAndConfirmationSchema} from './productionSchema';
import {ImagePlaceholderItem} from '../../components/productions/re-production/production-components/tiff-pdf-component/TiffPdfComponent';
import {FileTypeItem, RedactionData} from '../../api/types/production.type';

export const reProductionBasicInfoSchema = () =>
  z.object({
    name: z
      .string()
      .min(1, t('reProduction.validation.nameRequired'))
      .max(250, t('reProduction.validation.nameMaxLength'))
      .refine(
        (val) => !/[<>:"/\\|?*]/.test(val),
        t('reProduction.errors.nameRegex')
      ),
    description: z
      .string()
      .max(1000, t('reProduction.validation.descriptionMaxLength'))
      .optional()
      .or(z.literal('')),
    reProductionType: z.nativeEnum(ReProductionType, {
      message: t('reProduction.validation.typeRequired'),
    }),
  });

export type ReProductionBasicInfoFormData = z.infer<
  ReturnType<typeof reProductionBasicInfoSchema>
>;

export const outputSettingsSchema = () =>
  z
    .object({
      action: z.nativeEnum(FormAction),
      rootPath: z.string().optional(),
      directory: z
        .string()
        .trim()
        .min(1, {
          message: t('reProduction.outputSettings.errors.directoryRequired'),
        }),
      archiveProduction: z.boolean(),
      loadFilePathEnabled: z.boolean(),
      filePath: z.string().optional(),
      volumeIncluded: z.boolean(),
      volumeName: z.string().optional(),
      volumeLocation: z.string().optional(),
      nativeFolder: z.string().optional(),
      pdfFolder: z.string().optional(),
      textFolder: z.string().optional(),
      mp3Folder: z.string().optional(),
      imagesFolder: z.string().optional(),
      loadFilesFolder: z.string().optional(),
      translationsFolder: z.string().optional(),
      splitSubFolders: z.boolean(),
      splitCount: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.volumeIncluded) {
        if (!data.volumeName?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('reProduction.outputSettings.errors.volumeNameRequired'),
            path: ['volumeName'],
          });
        }

        if (!data.volumeLocation?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'reProduction.outputSettings.errors.volumeLocationRequired'
            ),
            path: ['volumeLocation'],
          });
        }
      }

      if (data.splitSubFolders) {
        if (!data.splitCount?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('reProduction.outputSettings.errors.splitCountRequired'),
            path: ['splitCount'],
          });
        } else if (!/^\d+$/.test(data.splitCount.trim())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('reProduction.outputSettings.errors.splitCountNumeric'),
            path: ['splitCount'],
          });
        }
      }
    });

export type OutputSettingsFormData = z.infer<
  ReturnType<typeof outputSettingsSchema>
>;

export type ReProductionQualityControlAndConfirmationFormData = z.infer<
  ReturnType<typeof qualityControlAndConfirmationSchema>
>;

export const ReProductionQualityControlAndConfirmationSchema = () =>
  z.object({
    action: z.nativeEnum(FormAction),
    // Add more validation rules as needed
  });

export const TextFormatEnum = z.enum(['ANSI', 'UnicodeUTF16']);
export const textComponentSchema = z.object({
  textFormat: TextFormatEnum.default('UnicodeUTF16'),
  eTextFormat: z.number().default(1),
  isAnsi: z.boolean().default(false),
  selectedANSIType: z.string().default('21027'),
  shouldSkipOCR: z.boolean().default(true),
  shouldExportLargeTextFiles: z.boolean().default(true),
  produceLoadFile: z.boolean().default(false),
});
export const translationComponentSchema = z.object({
  produceLoadFile: z.boolean().default(false),
  translationSelectedFileGroups: z.string().default(''),
});
// ── Components Schema ─────────────────────────────────────────────────────────
export const reproductionComponentsSchema = (
  messages: {
    datRequired: string;
    atLeastOneFieldRequired: string;
    prodBeginningBatesRequired: string;
    prodBeginningBatesOrDocIdRequired?: string;
    tagsRequired: string;
    fieldRequired: string;
    customBrandingTagsRequired?: string;
    customBrandingContentRequired?: string;
    customBrandingTagsAndTextRequired?: string;
    datFieldNameRequired: string;
    datFieldNameAlphabetStart: string;
    datFieldNameSpecialChars: string;
    datFieldNameDuplicate: string;
    placeholderPrivRequired?: string;
    placeholderTechIssueRequired?: string;
    placeholderNativeDocsRequired?: string;
    redactionTagsRequired?: string;
    redactionTextRequired?: string;
    generateMP3LoadFileRequired: string;
    mP3RedactionTagsEmpty: string;
    nativeFileGroupOrTagRequired: string;
  },
  moduleType?: ModuleType,
  // Skip native file group/tag validation when file settings are not editable
  isNativeFileSettingsEditable?: boolean
) =>
  z
    .object({
      action: z.nativeEnum(FormAction).default(FormAction.SAVE),
      datSelected: z
        .boolean()
        .superRefine((val, ctx) => {
          // For PRODUCTION, DAT is mandatory
          if (moduleType === ModuleType.PRODUCTION && !val) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: messages.datRequired,
            });
          }
        })
        .default(false),

      nativeSelected: z.boolean().default(true),
      nativeSelectedFileGroups: z.string().default(''),
      nativeSelectedTags: z.string().default(''),
      tiffSelected: z.boolean().default(false),
      textSelected: z.boolean().default(false),
      archiveSelected: z.boolean().default(false),
      translationSelected: z.boolean().default(false),
      mp3Selected: z.boolean().default(false),
      datFields: z
        .array(
          z.object({
            id: z.number(),
            category: z.string(),
            sourceField: z.string(),
            datFieldHeaderName: z
              .string()
              .refine((val) => val.trim().length > 0, {
                message: messages.datFieldNameRequired,
              })
              .refine(
                (val) => val.trim().length === 0 || /^[a-zA-Z]/.test(val),
                {
                  message: messages.datFieldNameAlphabetStart,
                }
              )
              .refine(
                (val) =>
                  val.trim().length === 0 || /^[a-zA-Z0-9_-]+$/.test(val),
                {
                  message: messages.datFieldNameSpecialChars,
                }
              ),
            scrubRedactions: z.boolean(),
            scrubPriv: z.boolean(),
            selectedTags: z
              .array(
                z.object({
                  id: z.string(),
                  label: z.string(),
                })
              )
              .optional(),
          })
        )
        .default([]),
      tiffPdfData: z
        .object({
          pageOption: z.number().optional(),
          pageType: z.number().optional(),
          pageSize: z.number().optional(),
          rotation: z.string().optional(),
          pageOrientation: z.number().optional(),
          skipNativeProduction: z.boolean().optional(),
          blankPageRemoval: z.boolean().optional(),
          preserveColor: z.boolean().optional(),
          brandingLocation: z.string().optional(),
          fontFamily: z.string().optional(),
          fontStyle: z.string().optional(),
          fontSize: z.string().optional(),
          colorJpgOption: z.string().optional(),
          documentSelectionMethod: z.string().optional(),
          selectedDocuments: z
            .array(
              z.object({
                value: z.union([z.string(), z.number()]).optional(),
                label: z.union([z.string(), z.number()]).optional(),
                disabled: z.boolean().optional(),
                isDefault: z.boolean().optional(),
              })
            )
            .optional(),
          selectedItemsForChips: z
            .array(
              z.object({
                id: z.string(),
                value: z.string(),
              })
            )
            .optional(),
          defaultBrandingData: z.record(z.string(), z.any()).optional(),
          customBrandingData: z
            .record(
              z.string(),
              z.array(
                z.object({
                  id: z.string(),
                  brandingData: z.object({
                    brandingText: z.string().nullable(),
                    selectedTags: z.array(
                      z.object({
                        tagId: z.number(),
                        tagLabel: z.string(),
                      })
                    ),
                    brandingLogicId: z.number(),
                    selectedTagList: z.string(),
                    brandingFont: z.string(),
                    brandingFontStyle: z.string(),
                    brandingFontSize: z.number(),
                  }),
                })
              )
            )
            .optional(),
          expandedPlusButtons: z.record(z.string(), z.boolean()).optional(),
          placeholderSettings: z
            .object({
              shouldApplyPlaceholderForErroredDocs: z.boolean().optional(),
              enabledforPrivDocs: z.boolean().optional(),
              enabledforNativeDocs: z.boolean().optional(),
              enabledforExceptionDocs: z.boolean().optional(),
              phImageTextPrivDocs: z.string().optional(),
              phImageTextTechIssueDocs: z.string().optional(),
              selectedPrivTags: z
                .array(
                  z.object({
                    tagId: z.number(),
                    tagLabel: z.string(),
                  })
                )
                .optional(),
              selectedTechIssueTags: z
                .array(
                  z.object({
                    tagId: z.number(),
                    tagLabel: z.string(),
                  })
                )
                .optional(),
              imagePlaceHolderList: z.array(z.any()).nullable().optional(),
            })
            .optional(),
          redactionSettings: z
            .object({
              isBurnRedactionSelected: z.boolean().optional(),
              isRedactionByAnnotation: z.boolean().optional(),
              redactionType: z.number().optional(),
              redactionStyle: z.string().optional(),
              redactionDataList: z.array(z.any()).optional(),
              specifiedRedactionList: z.string().optional(),
            })
            .optional(),
          redactionGlobalSelectedTags: z
            .array(
              z.object({
                tagId: z.number(),
                tagLabel: z.string(),
              })
            )
            .optional(),
        })
        .optional(),
      textComponentData: textComponentSchema.default(
        textComponentSchema.parse({})
      ),
      translationComponentModelData: translationComponentSchema.default(
        translationComponentSchema.parse({})
      ),
      mP3ComponentModelData: z
        .object({
          isBurnRedactionSelected: z.boolean().default(false),
          isRedactionByAnnotation: z.boolean().default(false),
          redactionType: z.string().default('0'),
          specifiedRedactionTagList: z.string().optional(), // no .refine here
          redactionStyle: z
            .object({
              value: z.string().default(''),
              label: z.number().default(0),
            })
            .nullable()
            .default({value: '', label: 0}),
          fillerAudioList: z.array(z.any()).default([]),
          produceLoadFile: z
            .boolean()
            .refine(Boolean, {
              message: messages.generateMP3LoadFileRequired,
            })
            .default(false),
        })
        .superRefine((data, ctx) => {
          if (
            data.isBurnRedactionSelected &&
            !data.isRedactionByAnnotation &&
            (!data.specifiedRedactionTagList ||
              data.specifiedRedactionTagList.trim() === '')
          ) {
            ctx.addIssue({
              code: 'custom',
              message: messages.mP3RedactionTagsEmpty,
              path: ['specifiedRedactionTagList'],
            });
          }
        })
        .default({
          isBurnRedactionSelected: false,
          isRedactionByAnnotation: false,
          redactionType: '0',
          specifiedRedactionTagList: '',
          redactionStyle: {value: '', label: 0},
          fillerAudioList: [],
          produceLoadFile: true,
        }),
    })
    .superRefine((data, ctx) => {
      // DAT fields validation - conditional based on moduleType and datSelected
      if (moduleType === ModuleType.PRODUCTION || data.datSelected) {
        // For PRODUCTION: always validate
        // For EXPORT: only validate if datSelected is true
        if (!data.datFields || data.datFields.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['datFields'],
            message: messages.atLeastOneFieldRequired,
          });
        } else {
          const hasEmptyFieldName = data.datFields.some(
            (field) =>
              !field.datFieldHeaderName ||
              field.datFieldHeaderName.trim() === ''
          );

          // PRODUCTION: Require a Field row with ProdBeginningBates (-146).
          // Only Field-category rows count; Tags rows do not satisfy this.
          if (moduleType === ModuleType.PRODUCTION) {
            const hasProdBeginningBates = data.datFields.some(
              (field) =>
                field.category?.toLowerCase() === 'field' &&
                field.sourceField === '-146'
            );
            if (!hasProdBeginningBates) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['datFields'],
                message: messages.prodBeginningBatesRequired,
              });
            }
          }

          // Tags validation
          const hasInvalidTags = data.datFields.some((field) => {
            if (field.category.toLowerCase() === 'tags') {
              return !field.selectedTags || field.selectedTags.length === 0;
            }
            return false;
          });
          if (hasInvalidTags) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['datFields'],
              message: messages.tagsRequired,
            });
          }

          // Field validation
          const hasInvalidField = data.datFields.some((field) => {
            if (field.category.toLowerCase() === 'field') {
              return !field.sourceField || field.sourceField.trim() === '';
            }
            return false;
          });
          if (hasInvalidField) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['datFields'],
              message: messages.fieldRequired,
            });
          }

          // Duplicate field names - only check if all fields have valid names
          // Empty field names should be excluded from duplicate checking
          // (field-level validation will handle empty names)
          if (!hasEmptyFieldName) {
            const fieldNames = data.datFields
              .map((field) => field.datFieldHeaderName.toLowerCase().trim())
              .filter((name) => name !== ''); // Filter out empty names
            const uniqueFieldNames = new Set(fieldNames);
            if (fieldNames.length !== uniqueFieldNames.size) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['datFields'],
                message: messages.datFieldNameDuplicate,
              });
            }
          }
        }
      }

      if (!data.tiffSelected) {
        return;
      }

      if (!data.tiffPdfData) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['tiffPdfData'],
          message: '',
        });
        return;
      }

      const customBrandingData = data.tiffPdfData.customBrandingData;
      if (customBrandingData) {
        for (const [_, brandingItems] of Object.entries(customBrandingData)) {
          if (Array.isArray(brandingItems) && brandingItems.length > 0) {
            for (const [_, item] of brandingItems.entries()) {
              const {brandingData} = item as {
                brandingData: {
                  brandingText: string;
                  selectedTags: unknown[];
                };
              };

              const isContentEmpty =
                !brandingData?.brandingText ||
                String(brandingData.brandingText).trim() === '';
              const isTagsEmpty =
                !brandingData?.selectedTags ||
                !Array.isArray(brandingData.selectedTags) ||
                brandingData.selectedTags.length === 0;

              if (isContentEmpty && isTagsEmpty) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: ['tiffPdfData', 'customBrandingData'],
                  message: messages.customBrandingTagsAndTextRequired || '',
                });
                continue;
              }

              if (isContentEmpty && !isTagsEmpty) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: ['tiffPdfData', 'customBrandingData'],
                  message: messages.customBrandingContentRequired || '',
                });
              }

              if (isTagsEmpty && !isContentEmpty) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: ['tiffPdfData', 'customBrandingData'],
                  message: messages.customBrandingTagsRequired || '',
                });
              }
            }
          }
        }
      }

      const placeholderSettings = data.tiffPdfData.placeholderSettings;
      if (placeholderSettings) {
        if (placeholderSettings.enabledforPrivDocs) {
          const hasPrivTags =
            placeholderSettings.selectedPrivTags &&
            Array.isArray(placeholderSettings.selectedPrivTags) &&
            placeholderSettings.selectedPrivTags.length > 0;
          const hasPrivText =
            placeholderSettings.phImageTextPrivDocs &&
            String(placeholderSettings.phImageTextPrivDocs).trim().length > 0;

          if (!hasPrivTags || !hasPrivText) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['tiffPdfData', 'placeholderSettings'],
              message: messages.placeholderPrivRequired || '',
            });
          }
        }

        if (placeholderSettings.enabledforExceptionDocs) {
          const hasTechIssueTags =
            placeholderSettings.selectedTechIssueTags &&
            Array.isArray(placeholderSettings.selectedTechIssueTags) &&
            placeholderSettings.selectedTechIssueTags.length > 0;
          const hasTechIssueText =
            placeholderSettings.phImageTextTechIssueDocs &&
            String(placeholderSettings.phImageTextTechIssueDocs).trim().length >
              0;

          if (!hasTechIssueTags || !hasTechIssueText) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['tiffPdfData', 'placeholderSettings'],
              message: messages.placeholderTechIssueRequired || '',
            });
          }
        }

        if (placeholderSettings.enabledforNativeDocs) {
          const imagePlaceHolderList =
            placeholderSettings.imagePlaceHolderList || [];
          const hasAtLeastOneItem = imagePlaceHolderList.length > 0;

          const allItemsValid =
            hasAtLeastOneItem &&
            imagePlaceHolderList.every((item: ImagePlaceholderItem) => {
              const hasPlaceholderText =
                item?.placeholderText &&
                String(item.placeholderText).trim().length > 0;
              const hasFileTypes =
                item?.selectedPHFileTypes &&
                Array.isArray(item.selectedPHFileTypes) &&
                item.selectedPHFileTypes.filter(
                  (ft: FileTypeItem) => ft?.isFileTypeSelected === true
                ).length > 0;
              const hasTags =
                item?.selectedPHTags &&
                Array.isArray(item.selectedPHTags) &&
                item.selectedPHTags.length > 0;
              return hasPlaceholderText && (hasFileTypes || hasTags);
            });

          if (!allItemsValid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [
                'tiffPdfData',
                'placeholderSettings',
                'enabledforNativeDocs',
              ],
              message: messages.placeholderNativeDocsRequired || '',
            });
          }
        }

        const redactionSettings = data.tiffPdfData.redactionSettings;
        const specifiedRedactionList =
          redactionSettings?.specifiedRedactionList || '';

        if (
          redactionSettings?.isBurnRedactionSelected &&
          redactionSettings.redactionType === 0
        ) {
          const hasRedactionTags =
            specifiedRedactionList && specifiedRedactionList.length > 0;

          if (!hasRedactionTags) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['tiffPdfData', 'redactionSettings'],
              message: messages.redactionTagsRequired || '',
            });
          }
        }

        if (redactionSettings?.isBurnRedactionSelected) {
          const redactionDataList = redactionSettings.redactionDataList || [];

          if (redactionDataList.length > 0) {
            const allItemsValid = redactionDataList.every(
              (item: RedactionData) => {
                const hasSelectedTags =
                  item?.selectedTags &&
                  Array.isArray(item.selectedTags) &&
                  item.selectedTags.length > 0;
                const hasRedactionText =
                  item?.redactionText &&
                  String(item.redactionText).trim().length > 0;
                const hasAbbrivatedText =
                  item?.abbrivatedText &&
                  String(item.abbrivatedText).trim().length > 0;

                return hasSelectedTags && hasRedactionText && hasAbbrivatedText;
              }
            );

            const hasMissingTags = redactionDataList.some(
              (item: RedactionData) => {
                return (
                  !item?.selectedTags ||
                  !Array.isArray(item.selectedTags) ||
                  item.selectedTags.length === 0
                );
              }
            );

            const hasMissingText = redactionDataList.some(
              (item: RedactionData) => {
                const hasRedactionText =
                  item?.redactionText &&
                  String(item.redactionText).trim().length > 0;
                const hasAbbrivatedText =
                  item?.abbrivatedText &&
                  String(item.abbrivatedText).trim().length > 0;
                return !hasRedactionText || !hasAbbrivatedText;
              }
            );

            if (!allItemsValid) {
              if (hasMissingText) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [
                    'tiffPdfData',
                    'redactionSettings',
                    'redactionDataList',
                  ],
                  message: messages.redactionTextRequired || '',
                });
              } else if (hasMissingTags) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [
                    'tiffPdfData',
                    'redactionSettings',
                    'redactionDataList',
                  ],
                  message: messages.redactionTagsRequired || '',
                });
              }
            }
          }
        }
      }
    })
    .superRefine((data, ctx) => {
      // Validate native component when file settings are editable
      if (data.nativeSelected && isNativeFileSettingsEditable) {
        // Check if file groups are present (handle empty string, null, undefined)
        const fileGroupsStr = data.nativeSelectedFileGroups
          ? String(data.nativeSelectedFileGroups).trim()
          : '';
        const hasFileGroups = fileGroupsStr.length > 0;

        // Check if tags are present (handle empty string, null, undefined)
        const tagsStr = data.nativeSelectedTags
          ? String(data.nativeSelectedTags).trim()
          : '';
        const hasTags = tagsStr.length > 0;

        // At least one file group OR tag is required
        if (!hasFileGroups && !hasTags) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nativeSelected'],
            message: messages.nativeFileGroupOrTagRequired,
          });
        }
      }
    });
export type ReProductionComponentsFormData = z.infer<
  ReturnType<typeof reproductionComponentsSchema>
>;
